import xss from 'xss';

import { Hidden } from 'react-grid-system';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { isMobile as isMobileDevice } from 'react-device-detect';
import CharacterCounter from '../CharacterCounter/CharacterCounter';
import ContentEditableDiv from '../ContentEditableDiv/ContentEditableDiv';
import EditIndicator from '../EditIndicator/EditIndicator';
import { EidCategory, Tag } from '@/client/common/structs/emoji/emoji';
import { addNotification } from '@/store/actions/notification';
import { useWindowResize } from '@/helpers';

import './_EditableHeadline.scss';

import { useAppDispatch } from '@/hooks';
import { Skeleton } from '@/components';

interface Props {
    editable: boolean;
    currentEmojiTags: Tag[];
    updateTagsQueue: (eidCategory: EidCategory, data?: string, deleteHashes?: string[]) => void;
    isLoading: boolean;
    setIsDirty?: (isDirty: boolean) => void;
}

const category = EidCategory.EMOJI_NICK;

function EditableHeadline({ currentEmojiTags, editable, updateTagsQueue, isLoading }: Props) {
    const { t } = useTranslation(['yat_page', 'words', 'translation']);
    const [currentCharCount, setCurrentCharCount] = useState(0);
    const placeholder = useMemo(() => t('headline_placeholder', { yat: t('translation:yat') }) as string, [t]);
    const [headlineValue, setHeadlineValue] = useState<string>(placeholder);

    const [isEditing, setIsEditing] = useState(false);
    const [showIndicator, setShowIndicator] = useState(false);
    const maxCharLimit = 60;

    const { width } = useWindowResize();

    const headline = useRef(headlineValue);
    const lastWidth = useRef(-1);
    const initialSetupIsComplete = useRef(false);

    const dispatch = useAppDispatch();

    const onWidthChange = useCallback((newWidth: number) => {
        if (!initialSetupIsComplete.current) {
            return;
        }
        lastWidth.current = newWidth;
    }, []);

    const onHeadlineChange = useCallback((text: string) => {
        // remove line breaks
        text = text.replace(/(\r\n|\n|\r)/gm, '');
        if (text === headline.current) {
            return;
        }
        setCurrentCharCount(text.length);
        headline.current = text;
    }, []);

    useLayoutEffect(() => {
        if (isLoading || currentEmojiTags.length === 0) {
            return;
        }
        const headlineRecord = currentEmojiTags.find((t) => t.tag === category);

        if (headlineRecord == null) {
            return;
        }

        const headlineRecordData = xss(headlineRecord.data);
        if (headline.current === headlineRecordData) {
            // headline hasn't changed
            return;
        }
        setHeadlineValue(headlineRecordData);
    }, [isLoading, currentEmojiTags, editable, onHeadlineChange]);

    useEffect(() => {
        if (width !== undefined) {
            onWidthChange(width);
        }
    }, [width, onWidthChange]);

    function onBlurred() {
        const deleteTags = currentEmojiTags.filter((t) => t.tag === category).map((t) => t.hash);
        //If they haven't typed in any value or backspaced it then just delete the tag
        if (headline.current && headline.current.length > 0) {
            updateTagsQueue(category, headline.current, deleteTags);
        } else {
            dispatch(
                addNotification({
                    title: t('headline_required'),
                    message: t('cool_headline_message'),
                    severity: 'alert',
                }),
            );
            updateTagsQueue(category, undefined, deleteTags);
        }
    }

    function onEditing(editing: boolean) {
        setIsEditing(editing);
    }

    return (
        <Skeleton on={isLoading} variant="headline">
            <div className="editable-headline">
                <div className="indicator-holder">
                    {isEditing && <CharacterCounter maxChars={maxCharLimit} currentChars={currentCharCount} />}
                    {!isEditing && (
                        <Hidden xs sm>
                            <EditIndicator show={!isMobileDevice && showIndicator} fieldName={t('words:headline')} />
                        </Hidden>
                    )}
                </div>
                {editable ? (
                    <div
                        id="headline"
                        className="headline"
                        style={{ letterSpacing: '-1px' }}
                        onMouseEnter={() => setShowIndicator(true)}
                        onMouseLeave={() => setShowIndicator(false)}
                    >
                        <ContentEditableDiv
                            value={headlineValue || placeholder}
                            placeholder={placeholder}
                            onValueChange={onHeadlineChange}
                            onBlurred={onBlurred}
                            charLimit={maxCharLimit}
                            onEditing={(editing) => onEditing(editing)}
                            isEditing={isEditing}
                        />
                    </div>
                ) : (
                    <div id="headline" className="headline not-editable" style={{ letterSpacing: '-1px' }}>
                        {headlineValue || placeholder}
                    </div>
                )}
            </div>
        </Skeleton>
    );
}

export default EditableHeadline;
